import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import type { HierarchicalItem } from "../../../types/uiTypes";

type ModalState<T> = {
  type: "create" | "edit";
  item?: T;
  parentItem?: T;
};

type UseModalStateProps<T> = {
  getParentItem: (item: T) => T | undefined;
};

export function useModalState<T extends HierarchicalItem>({
  getParentItem,
}: UseModalStateProps<T>) {
  const [_searchParams, setSearchParams] = useSearchParams();
  const [modal, setModal] = useState<ModalState<T> | null>(null);
  const [deleteModal, setDeleteModal] = useState<T | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{
    action: () => void;
  } | null>(null);

  const resetModalState = useCallback(() => {
    setModal(null);
    setSearchParams({});
    setHasUnsavedChanges(false);
  }, [setSearchParams]);

  const withUnsavedChangesGuard = useCallback(
    (action: () => void) => {
      if (hasUnsavedChanges) {
        setPendingNavigation({
          action: () => {
            action();
            setHasUnsavedChanges(false);
          },
        });
      } else {
        action();
      }
    },
    [hasUnsavedChanges],
  );

  const openCreateModal = useCallback(
    (parentItem?: T) => {
      withUnsavedChangesGuard(() => {
        setModal({ type: "create", parentItem });
      });
    },
    [withUnsavedChangesGuard],
  );

  const openEditModal = useCallback(
    (item: T, updateUrl = true) => {
      const parentItem = getParentItem(item);
      setModal({ type: "edit", item, parentItem });
      if (updateUrl) {
        setSearchParams({ edit: item.id.toString() });
      }
    },
    [getParentItem, setSearchParams],
  );

  const closeModal = useCallback(() => {
    withUnsavedChangesGuard(resetModalState);
  }, [withUnsavedChangesGuard, resetModalState]);

  return {
    modal,
    setModal,
    deleteModal,
    setDeleteModal,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    pendingNavigation,
    setPendingNavigation,
    resetModalState,
    withUnsavedChangesGuard,
    openCreateModal,
    openEditModal,
    closeModal,
  };
}
